import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Select } from '@/components/ui';
import { ShortageType, ShortageTypeLabels } from '@/types/models';

const shortageSchema = z.object({
  order_item_id: z.coerce.number().min(1, 'Выберите позицию'),
  shortage_type: z.string().min(1, 'Выберите тип'),
  description: z.string().optional(),
});

type ShortageFormData = z.infer<typeof shortageSchema>;

interface ShortageFormProps {
  orderId: number;
  deliveryId: number;
  orderItems: Array<{ id: number; name: string; sku: string }>;
  onSubmit: (data: { order_item_id: number; shortage_type: string; description?: string }) => Promise<void>;
  isSubmitting?: boolean;
}

const shortageTypeOptions = Object.entries(ShortageTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

const ShortageForm: React.FC<ShortageFormProps> = ({ orderItems, onSubmit, isSubmitting }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ShortageFormData>({
    resolver: zodResolver(shortageSchema) as never,
    defaultValues: {
      order_item_id: 0,
      shortage_type: ShortageType.MISSING,
      description: '',
    },
  });

  const handleFormSubmit = async (data: ShortageFormData) => {
    await onSubmit(data);
    reset();
  };

  const itemOptions = orderItems.map((item) => ({
    value: String(item.id),
    label: `${item.sku} — ${item.name}`,
  }));

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Select
        label="Позиция"
        options={itemOptions}
        placeholder="Выберите позицию"
        error={errors.order_item_id?.message}
        required
        {...register('order_item_id')}
      />

      <Select
        label="Тип недостачи"
        options={shortageTypeOptions}
        error={errors.shortage_type?.message}
        required
        {...register('shortage_type')}
      />

      <Input
        label="Описание"
        placeholder="Опишите проблему (необязательно)"
        {...register('description')}
      />

      <Button type="submit" variant="danger" loading={isSubmitting} className="w-full">
        Сообщить о недостаче
      </Button>
    </form>
  );
};

export default ShortageForm;
